import * as React from 'react';
import { instructionApi, type AlertLinkView, type MonitoringCheckView, type PaymentDocumentView, type ScreeningCallView } from '@ps/api-client/console';
import { Alert } from '@ps/ui-kit/components/alert';
import { Details } from '@ps/ui-kit/components/card';
import { AttachmentList } from '@ps/ui-kit/components/file-button';
import { Pill, type Tone } from '@ps/ui-kit/components/pill';
import { Table } from '@ps/ui-kit/components/table';
import { openBlob } from '@ps/ui-kit/lib/download';
import { dateTime } from '@ps/ui-kit/lib/format';

// Evidence shared by the payment pages (K-06 instruction, refused evaluation): the SCR-03 screening calls, the vendor's
// transaction monitoring check (ADR-108), the alerts the payment raised and the supporting documents the customer attached
// (ADR-090; each opening is audited).

export function ScreeningCalls({ calls }: { calls: ScreeningCallView[] }) {
  if (!calls.length) return <p className="text-sm text-ink-3">No counterparty screening was recorded.</p>;
  return (
    <div className="overflow-x-auto">
      <Table bare compact label="Screening calls">
        <thead><tr><th>Subject</th><th>Outcome</th><th>Vendor ref</th>
          <th>List</th><th className="text-right">Latency</th><th>Hits</th></tr></thead>
        <tbody>
          {calls.map(s => (
            <tr key={`${s.subjectRole}-${s.subjectName}`} className="align-top">
              <td><span className="font-semibold">{s.subjectName}</span><span className="block text-ink-3">{s.subjectRole.replace('_', ' ').toLowerCase()} · {s.subjectKind.toLowerCase()}</span></td>
              <td><Pill tone={s.outcome === 'CLEAR' ? 'ok' : 'bad'} upper>{s.outcome.replace('_', ' ')}</Pill>{s.error && <span className="block text-bad">{s.error}</span>}</td>
              <td className="font-mono">{s.vendorRef ?? '—'}<span className="block text-ink-3">{s.vendor}</span></td>
              <td>{s.listVersion ?? '—'}</td>
              <td className="text-right tabular">{s.latencyMs} ms</td>
              <td>{s.hits.length ? s.hits.map(h => <span key={h.listedName} className="block">{h.listedName} · {h.score} · {h.types.join(', ')}</span>) : '—'}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
}

const DECISION: Record<NonNullable<MonitoringCheckView['decision']>, { label: string; tone: Tone }> = {
  APPROVE: { label: 'Approved', tone: 'ok' }, WATCH: { label: 'Watch', tone: 'warn' }, REVIEW: { label: 'Manual review', tone: 'warn' },
  DECLINE: { label: 'Declined', tone: 'bad' }, CUSTOM: { label: 'Unknown action', tone: 'warn' },
};

/** ADR-108: the vendor's answer and the rules that fired. A check that did not complete says so in plain words, with the
 *  reference to quote to the tech team; the technical detail is in the operations log, not here. */
export function MonitoringCheck({ check }: { check: MonitoringCheckView | null }) {
  if (!check) return <p className="text-sm text-ink-3">Transaction monitoring was not called for this payment.</p>;
  const ref = <span className="font-mono">{check.reference}</span>;
  return (
    <div className="space-y-3 text-sm">
      {check.completed && check.decision ? (
        <>
          <p><Pill tone={DECISION[check.decision].tone} upper>{DECISION[check.decision].label}</Pill>
            {!check.ruleHits.length && <span className="ml-2 text-ink-3">No rule fired.</span>}</p>
          {check.ruleHits.length > 0 && (
            <div className="overflow-x-auto">
              <Table bare compact label="Rules that fired">
                <thead><tr><th>Rule</th><th>Action</th><th>Version</th></tr></thead>
                <tbody>{check.ruleHits.map(h => <tr key={`${h.name}-${h.version}`}><td className="font-semibold">{h.name}</td><td>{h.action}</td><td>{h.version ?? '—'}</td></tr>)}</tbody>
              </Table>
            </div>
          )}
        </>
      ) : (
        <Alert tone="warn" title="Check not completed">
          {check.notCompletedReason ? `${check.notCompletedReason.charAt(0).toUpperCase()}${check.notCompletedReason.slice(1)}. ` : ''}
          The payment was held because the check could not run, not because anything was found.{' '}
          If you need the cause, give the tech team reference {ref}.
        </Alert>
      )}
      <p className="text-xs text-ink-3">
        Rule set {check.ruleSets.join(', ')} · reference {ref}
        {check.vendorTransactionId && <> · Shufti transaction <span className="font-mono">{check.vendorTransactionId}</span></>} · {check.latencyMs} ms · {dateTime(check.checkedAt)}
        {check.flags.length > 0 && <> · our facts sent: {check.flags.join(', ')}</>}
      </p>
    </div>
  );
}

/** The alerts a payment raised, each opening its workspace in the queue it belongs to. */
/** Who the money was going to, frozen as sent (instruction page and alert workspace). A transfer to another PayStreet
 *  account names the account instead of a bank. */
export const beneficiaryTitle = (b: Record<string, string>) => (b.paystreetAccount ? 'Recipient: PayStreet account (frozen)' : 'Beneficiary (frozen)');
export function BeneficiarySnapshot({ snapshot: b }: { snapshot: Record<string, string> }) {
  return b.paystreetAccount
    ? <Details rows={[['Name', b.name], ['PayStreet account', <span key="a" className="font-mono">{b.paystreetAccount}</span>], ['Institution', 'PayStreet (internal transfer)']]} />
    : <Details rows={[['Name', b.name], ['Bank', b.bank], ['BIC', b.bic], ['Country', b.country], ['Account', b.account], ['Address', b.address]]} />;
}

export function AlertLinks({ alerts, onOpen }: { alerts: AlertLinkView[]; onOpen?: (a: AlertLinkView) => void }) {
  return (
    <ul className="mt-1 text-sm">{alerts.map(a => (
      <li key={a.id}>
        {onOpen
          ? <button className="font-mono font-semibold text-tick-ink underline" onClick={() => onOpen(a)}>{a.reference}</button>
          : <span className="font-mono">{a.reference}</span>}
        {' '}· {a.scenarioId} · {a.state.replaceAll('_', ' ').toLowerCase()} · {a.escalated ? 'C2' : 'C1'} · SLA {dateTime(a.slaDue)}
      </li>
    ))}</ul>
  );
}

export function PaymentDocuments({ files, app = 'console', label = 'Supporting documents' }: { files: PaymentDocumentView[]; app?: 'admin' | 'console'; label?: string }) {
  const [error, setError] = React.useState<string | null>(null);
  if (!files.length) return <p className="text-sm text-ink-3">The customer attached no documents.</p>;
  const open = async (id: string) => {
    setError(null);
    try { openBlob(await instructionApi(app).paymentDocument(id)); } catch { setError("We couldn't open the document. Please try again."); }
  };
  return (
    <div className="space-y-2">
      <AttachmentList files={files.map(f => ({ ...f, tag: f.source === 'RFI_ANSWER' ? 'Answer to a request' : f.source === 'PAYMENT' ? 'With the payment' : undefined }))}
        onOpen={id => void open(id)} label={label} />
      {error && <Alert tone="bad">{error}</Alert>}
    </div>
  );
}
